import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Input,
  Form,
  Modal,
  message,
  Space,
  Popconfirm,
  Select,
  Switch,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  getAllPartners,
  createPartner,
  updatePartner,
  deletePartner,
  clearPartnersCache,
} from "../../api/accounting";
import { fetchBrandsBySelectedClients } from "../../../../core/api/settings";
import { useSelector } from "react-redux";
import useGlobalFilters from "../../../../hooks/useGlobalFilters";

const { Option } = Select;

const PartnerTab = ({ selectedBrand = null }) => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const [activeFilter, setActiveFilter] = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);
  const [brandFilter, setBrandFilter] = useState(null);
  const [brands, setBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // Force re-render counter

  // Get selected clients from Redux
  const selectedClients = useSelector((state) => state.selectedClients);
  const { selectedBrands: globalSelectedBrands } = useGlobalFilters();

  // Build effective brands list: use all global brands, fallback to single prop
  const effectiveBrands = React.useMemo(() => {
    if (globalSelectedBrands && globalSelectedBrands.length > 0) {
      return globalSelectedBrands;
    }
    if (selectedBrand) {
      return [selectedBrand];
    }
    return [];
  }, [globalSelectedBrands, selectedBrand]);

  const effectiveBrandIds = React.useMemo(
    () => effectiveBrands.map((b) => b.id).join(","),
    [effectiveBrands]
  );

  // Fetch all partners when selected brands change
  useEffect(() => {
    fetchPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveBrandIds]);

  // Fetch brands when component mounts or selectedClients change
  useEffect(() => {
    fetchBrands();
  }, [selectedClients]);

  const fetchBrands = async () => {
    if (!selectedClients || selectedClients.length === 0) {
      setBrands([]);
      return;
    }

    setLoadingBrands(true);
    try {
      const response = await fetchBrandsBySelectedClients(selectedClients);
      setBrands(response.data || []);
    } catch (error) {
      console.error("Error fetching brands:", error);
      message.error("Failed to fetch brands");
      setBrands([]);
    } finally {
      setLoadingBrands(false);
    }
  };

  // Track current fetch to prevent race conditions
  const fetchIdRef = React.useRef(0);

  const fetchPartners = async (filters = {}, useCurrentFilters = false) => {
    // Increment fetch ID to track this request
    const currentFetchId = ++fetchIdRef.current;

    setLoading(true);
    try {
      // If no brands are selected, show empty list
      if (effectiveBrands.length === 0) {
        setPartners([]);
        setLoading(false);
        return;
      }

      // Send all selected brand IDs as comma-separated (backend supports this)
      filters.brand = effectiveBrands.map((b) => b.id).join(",");

      // If useCurrentFilters is true, apply the current search/filter state
      if (useCurrentFilters) {
        if (searchText) filters.name = searchText;
        if (activeFilter !== null) filters.active = activeFilter;
      }

      // Get all partners (vendors and customers) for this brand only
      const response = await getAllPartners(filters);

      // Only update state if this is still the latest request (prevents race conditions)
      if (currentFetchId !== fetchIdRef.current) {
        return; // A newer request has been made, ignore this result
      }

      // Handle both direct array response and wrapped response
      let partnersData = response.data;

      if (Array.isArray(partnersData)) {
        setPartners([...partnersData]); // Force new array reference
      } else if (partnersData && Array.isArray(partnersData.results)) {
        setPartners([...partnersData.results]); // Force new array reference
      } else {
        setPartners([]);
      }
    } catch (error) {
      // Only handle error if this is still the latest request
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }
      console.error("PartnerTab: Error fetching partners:", error);
      const errorMsg =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        "Unknown error";
      message.error("Failed to fetch partners: " + errorMsg);
      setPartners([]);
    } finally {
      // Only update loading if this is still the latest request
      if (currentFetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  };

  const handleSearch = () => {
    const filters = {};
    if (searchText) filters.name = searchText;
    if (activeFilter !== null) filters.active = activeFilter;
    if (typeFilter) filters.type = typeFilter;
    fetchPartners(filters);
  };

  const resetSearch = () => {
    setSearchText("");
    setActiveFilter(null);
    setTypeFilter(null);
    setBrandFilter(null);
    fetchPartners();
  };

  const showCreateModal = () => {
    setEditingPartner(null);
    form.resetFields();
    // Set default values
    form.setFieldsValue({
      partner_type: "vendor",
      active: true,
    });
    setIsModalVisible(true);
  };

  const showEditModal = (partner) => {
    setEditingPartner(partner);
    // Extract brand IDs from brands array (could be objects or IDs)
    let brandIds = [];
    if (partner.brands && Array.isArray(partner.brands)) {
      brandIds = partner.brands.map((b) => (typeof b === "object" ? b.id : b));
    } else if (partner.brand_ids && Array.isArray(partner.brand_ids)) {
      brandIds = partner.brand_ids;
    } else if (partner.brand) {
      // Fallback to single brand
      brandIds = [
        typeof partner.brand === "object" ? partner.brand.id : partner.brand,
      ];
    }

    form.setFieldsValue({
      name: partner.name,
      email: partner.email || "",
      phone: partner.phone || "",
      address: partner.address || "",
      city: partner.city || "",
      state: partner.state || "",
      zip_code: partner.zip_code || "",
      country: partner.country || "",
      tax_number: partner.tax_number || "",
      website: partner.website || "",
      notes: partner.notes || "",
      partner_type: partner.partner_type || "vendor",
      active: partner.active,
      brands: brandIds, // Set selected brands as IDs
    });
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Make sure address fields are included
      const fieldsToCheck = [
        "city",
        "state",
        "zip_code",
        "country",
        "email",
        "phone",
        "address",
        "tax_number",
        "website",
        "notes",
      ];
      fieldsToCheck.forEach((field) => {
        if (values[field] === undefined) {
          values[field] = "";
        }
      });

      // Handle brand assignment - use legacy brand field for backward compatibility
      if (effectiveBrands.length > 0) {
        values.brand = effectiveBrands[0].id;
      } else if (selectedBrand) {
        values.brand = selectedBrand.id;
      }

      // Validate that at least one brand is selected (either legacy or new brands field)
      if (!values.brands || values.brands.length === 0) {
        if (effectiveBrands.length === 0 && !selectedBrand) {
          message.error("Please select at least one brand for this partner");
          return;
        }
      }

      // Ensure all required fields have values
      if (!values.partner_type) values.partner_type = "vendor";
      if (values.active === undefined) values.active = true;

      if (editingPartner) {
        const response = await updatePartner(editingPartner.id, values);
        // Handle both axios wrapped response and direct data response
        const updatedPartner = response?.data?.id
          ? response.data
          : response?.id
          ? response
          : { ...editingPartner, ...values };
        message.success("Partner updated successfully");

        // Immediately update the item in state (optimistic update)
        setPartners((prevPartners) => {
          const newPartners = prevPartners.map((p) =>
            p.id === editingPartner.id
              ? { ...p, ...values, ...updatedPartner }
              : p
          );
          return [...newPartners]; // Force new array reference
        });
      } else {
        // CREATE NEW PARTNER
        await createPartner(values);

        message.success("Partner created successfully");

        // Close modal
        setIsModalVisible(false);
        form.resetFields();

        // Clear search filters to show all partners
        setSearchText("");
        setActiveFilter(null);

        // Clear partners cache so other components fetch fresh data
        clearPartnersCache();

        // Refetch partners to get the newly created one with server-generated fields
        // This is more reliable than optimistic updates for create operations
        await fetchPartners();

        // Force table re-render just in case
        setRefreshKey((prev) => prev + 1);

        // Dispatch event to notify other components to refresh partners
        window.dispatchEvent(new CustomEvent("partnersUpdated"));

        return; // Exit early for create path
      }

      // Clear partners cache so other components fetch fresh data
      clearPartnersCache();

      setIsModalVisible(false);
      form.resetFields();

      // Force table re-render
      setRefreshKey((prev) => prev + 1);

      // Clear search filters when adding/editing to show all partners
      setSearchText("");
      setActiveFilter(null);

      // Dispatch event to notify other components to refresh partners
      window.dispatchEvent(new CustomEvent("partnersUpdated"));
    } catch (error) {
      console.error("Error saving partner:", error.response?.data || error);
      message.error(
        "Failed to save partner: " +
          (error.response?.data?.detail || error.message)
      );
    }
  };

  const handleDelete = async (partnerId) => {
    try {
      await deletePartner(partnerId);
      message.success("Partner deleted successfully");

      // Clear partners cache so other components fetch fresh data
      clearPartnersCache();

      // Immediately remove the deleted item from state (optimistic update)
      setPartners((prevPartners) =>
        prevPartners.filter((p) => p.id !== partnerId)
      );

      // Force table re-render
      setRefreshKey((prev) => prev + 1);

      // Dispatch event to notify other components to refresh partners
      window.dispatchEvent(new CustomEvent("partnersUpdated"));
    } catch (error) {
      console.error("Error deleting partner:", error);
      message.error("Failed to delete partner");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
    },
    {
      title: "Type",
      dataIndex: "partner_type",
      key: "partner_type",
      filters: [
        { text: "Vendor", value: "vendor" },
        { text: "Customer", value: "customer" },
        { text: "Both", value: "both" },
      ],
      filteredValue: typeFilter ? [typeFilter] : null,
      onFilter: (value, record) => record.partner_type === value,
      render: (type) => {
        switch (type) {
          case "vendor":
            return "Vendor";
          case "customer":
            return "Customer";
          case "both":
            return "Both";
          default:
            return type;
        }
      },
    },
    {
      title: "Brands",
      dataIndex: "brand_names",
      key: "brand_names",
      filters: brands.map((b) => ({ text: b.brand_name, value: b.brand_name })),
      filteredValue: brandFilter ? [brandFilter] : null,
      onFilter: (value, record) =>
        record.brand_names && record.brand_names.includes(value),
      render: (brandNames) => {
        if (!brandNames || brandNames.length === 0) return "No brands";
        return brandNames.join(", ");
      },
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      render: (active) => (active ? "Active" : "Inactive"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => showEditModal(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this vendor?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="danger" icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card title="Partners Management">
      <Space style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search by name"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onPressEnter={handleSearch}
          style={{ width: 200 }}
          prefix={<SearchOutlined />}
        />
        <Select
          placeholder="Filter by type"
          allowClear
          style={{ width: 150 }}
          value={typeFilter}
          onChange={(value) => setTypeFilter(value)}
        >
          <Option value="vendor">Vendor</Option>
          <Option value="customer">Customer</Option>
          <Option value="both">Both</Option>
        </Select>
        <Select
          placeholder="Filter by brand"
          allowClear
          style={{ width: 180 }}
          value={brandFilter}
          onChange={(value) => setBrandFilter(value)}
          showSearch
          optionFilterProp="children"
        >
          {brands.map((b) => (
            <Option key={b.id} value={b.brand_name}>
              {b.brand_name}
            </Option>
          ))}
        </Select>
        <Select
          placeholder="Filter by status"
          allowClear
          style={{ width: 150 }}
          value={activeFilter}
          onChange={(value) => setActiveFilter(value)}
        >
          <Option value={true}>Active</Option>
          <Option value={false}>Inactive</Option>
        </Select>
        <Button type="primary" onClick={handleSearch}>
          Search
        </Button>
        <Button onClick={resetSearch}>Reset</Button>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={showCreateModal}
        >
          Add Partner
        </Button>
      </Space>

      <Table
        key={refreshKey}
        columns={columns}
        dataSource={partners}
        rowKey="id"
        loading={loading}
        onChange={(_pagination, filters) => {
          if (filters.partner_type) {
            setTypeFilter(filters.partner_type[0] || null);
          } else {
            setTypeFilter(null);
          }
          if (filters.brand_names) {
            setBrandFilter(filters.brand_names[0] || null);
          } else {
            setBrandFilter(null);
          }
        }}
        pagination={{
          defaultPageSize: 50,
          showSizeChanger: true,
          pageSizeOptions: ["25", "50", "100", "200"],
          showTotal: (total) => `Total ${total} partners`,
        }}
      />

      <Modal
        title={editingPartner ? "Edit Partner" : "Add Partner"}
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={handleCancel}
        width={800}
        okText={editingPartner ? "Update" : "Create"}
        styles={{ mask: { zIndex: 9999 } }}
        style={{ zIndex: 10000 }}
        zIndex={10000}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Partner Name"
            rules={[{ required: true, message: "Please enter partner name" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[{ type: "email", message: "Please enter a valid email" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="phone" label="Phone">
            <Input />
          </Form.Item>

          <Form.Item name="address" label="Address">
            <Input.TextArea rows={2} />
          </Form.Item>

          <div style={{ display: "flex", gap: "16px" }}>
            <Form.Item name="city" label="City" style={{ flex: 1 }}>
              <Input />
            </Form.Item>

            <Form.Item name="state" label="State/Province" style={{ flex: 1 }}>
              <Input />
            </Form.Item>
          </div>

          <div style={{ display: "flex", gap: "16px" }}>
            <Form.Item
              name="zip_code"
              label="ZIP/Postal Code"
              style={{ flex: 1 }}
            >
              <Input />
            </Form.Item>

            <Form.Item name="country" label="Country" style={{ flex: 1 }}>
              <Input />
            </Form.Item>
          </div>

          <Form.Item name="tax_number" label="Tax Number">
            <Input />
          </Form.Item>

          <Form.Item
            name="website"
            label="Website"
            rules={[{ type: "url", message: "Please enter a valid URL" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item
            name="partner_type"
            label="Partner Type"
            initialValue="vendor"
            rules={[{ required: true, message: "Please select partner type" }]}
          >
            <Select>
              <Option value="vendor">Vendor</Option>
              <Option value="customer">Customer</Option>
              <Option value="both">Both</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="brands"
            label="Brands"
            rules={[
              { required: true, message: "Please select at least one brand" },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Select brands this partner works with"
              loading={loadingBrands}
              allowClear
            >
              {brands.map((brand) => (
                <Option key={brand.id} value={brand.id}>
                  {brand.brand_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="active"
            label="Active"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default PartnerTab;
